import React, { useEffect, useMemo, useState } from "react";
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  getDocs,
} from "firebase/firestore";

/**
 * Firestore shape (per doc in "sales_rep"):
 * {
 *   name: string,
 *   email: string,
 *   phone: string,
 *   postcodes: string[]  // array of outcodes like "TF","N","EC","BN"
 * }
 */

const SalesRepManager = () => {
  const db = getFirestore();

  // --- search
  const [postCode, setPostCode] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  // --- admin list
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- add new rep
  const [newRep, setNewRep] = useState({ name: "", email: "", phone: "", postcodes: "" });
  const [adding, setAdding] = useState(false);

  // --- feedback
  const [toast, setToast] = useState({ type: "", msg: "" });

  // util: normalize an input postcode to OUTCODE (letters before first digit)
  const extractOutcode = (raw) => {
    if (!raw) return "";
    const cleaned = raw.toUpperCase().replace(/\s+/g, "");
    const m = cleaned.match(/^([A-Z]+)\d/);
    return m ? m[1] : cleaned.replace(/[^A-Z]/g, "");
  };

  const normalizeOutcodeArray = (rawList) => {
    // rawList can be comma-separated string or array; return unique, sorted array of A–Z strings
    const arr = Array.isArray(rawList)
      ? rawList
      : String(rawList || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

    const normalized = arr
      .map(extractOutcode)
      .filter(Boolean);

    // unique + sort
    return Array.from(new Set(normalized)).sort();
  };

  // live list
  useEffect(() => {
    const colRef = collection(db, "sales_rep");
    const unsub = onSnapshot(
      colRef,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // sort by name asc
        rows.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setReps(rows);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        setToast({ type: "error", msg: `Failed to load reps: ${err.message}` });
      }
    );
    return () => unsub();
  }, [db]);

  // search
  const doSearch = async () => {
    setSearchError("");
    setSearchResults([]);
    const q = postCode.trim();
    if (!q) return;

    setSearching(true);
    try {
      const colRef = collection(db, "sales_rep");
      const snap = await getDocs(colRef);
      const out = extractOutcode(q);
      const matches = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((rep) => Array.isArray(rep.postcodes) && rep.postcodes.includes(out));
      if (matches.length === 0) {
        setSearchError("No sales rep found for this postcode.");
      } else {
        setSearchResults(matches);
      }
    } catch (e) {
      setSearchError("An error occurred while searching.");
    } finally {
      setSearching(false);
    }
  };

  // add new
  const addNewRep = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const payload = {
        name: newRep.name.trim(),
        email: newRep.email.trim(),
        phone: newRep.phone.trim(),
        postcodes: normalizeOutcodeArray(newRep.postcodes),
      };

      if (!payload.name || !payload.email) {
        throw new Error("Name and email are required.");
      }
      await addDoc(collection(db, "sales_rep"), payload);
      setNewRep({ name: "", email: "", phone: "", postcodes: "" });
      setToast({ type: "success", msg: "Rep added." });
    } catch (e) {
      setToast({ type: "error", msg: `Add failed: ${e.message}` });
    } finally {
      setAdding(false);
    }
  };

  // edit/save a rep
  const saveRep = async (repId, draft) => {
    try {
      const payload = {
        name: (draft.name || "").trim(),
        email: (draft.email || "").trim(),
        phone: (draft.phone || "").trim(),
        postcodes: normalizeOutcodeArray(draft.postcodes),
      };
      if (!payload.name || !payload.email) throw new Error("Name and email are required.");
      await updateDoc(doc(db, "sales_rep", repId), payload);
      setToast({ type: "success", msg: "Rep updated." });
    } catch (e) {
      setToast({ type: "error", msg: `Update failed: ${e.message}` });
    }
  };

  const deleteRep = async (repId) => {
    if (!window.confirm("Delete this sales rep? This cannot be undone.")) return;
    try {
      await deleteDoc(doc(db, "sales_rep", repId));
      setToast({ type: "success", msg: "Rep deleted." });
    } catch (e) {
      setToast({ type: "error", msg: `Delete failed: ${e.message}` });
    }
  };

  // local edit state per rep id
  const [drafts, setDrafts] = useState({});
  useEffect(() => {
    // initialize drafts when reps change
    const next = {};
    for (const r of reps) {
      next[r.id] = {
        name: r.name || "",
        email: r.email || "",
        phone: r.phone || "",
        postcodes: (r.postcodes || []).join(", "),
        newCode: "", // helper for adding a single code
      };
    }
    setDrafts(next);
  }, [reps]);

  const setDraft = (id, patch) => {
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));
  };

  const addSinglePostcode = (id) => {
    const d = drafts[id];
    if (!d) return;
    const merged = normalizeOutcodeArray([...(d.postcodes || "").split(","), d.newCode]);
    setDraft(id, { postcodes: merged.join(", "), newCode: "" });
  };

  const removeSinglePostcode = (id, code) => {
    const d = drafts[id];
    if (!d) return;
    const arr = normalizeOutcodeArray(d.postcodes);
    const filtered = arr.filter((c) => c !== code);
    setDraft(id, { postcodes: filtered.join(", ") });
  };

  // small toast
  useEffect(() => {
    if (!toast.msg) return;
    const t = setTimeout(() => setToast({ type: "", msg: "" }), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  return (
    <div className="mx-auto max-w-5xl p-6">
      {/* Header */}
      <h1 className="text-2xl font-bold mb-4">Sales Rep Manager</h1>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3 text-[#606bbf]">Find Rep by Postcode</h2>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Enter postcode (e.g., TF3 1SP or N1)"
            value={postCode}
            onChange={(e) => setPostCode(e.target.value)}
            className="flex-1 p-3 border rounded-md focus:ring-2 focus:ring-[#606bbf] focus:outline-none"
          />
          <button
            onClick={doSearch}
            className="px-4 py-2 bg-[#606bbf] hover:bg-[#4c54a1] text-white rounded-md font-semibold"
          >
            Search
          </button>
        </div>
        {searching && <p className="mt-2 text-gray-500">Searching…</p>}
        {searchError && <p className="mt-2 text-red-600">{searchError}</p>}
        {searchResults.length > 0 && (
          <div className="mt-4">
            <h3 className="font-semibold">Results:</h3>
            <div className="mt-2 grid gap-3">
              {searchResults.map((rep) => (
                <div key={rep.id} className="p-3 border rounded bg-gray-50">
                  <p><strong>Name:</strong> {rep.name}</p>
                  <p>
                    <strong>Email:</strong>{" "}
                    <a className="text-blue-600" href={`mailto:${rep.email}`}>{rep.email}</a>
                  </p>
                  <p><strong>Phone:</strong> {rep.phone}</p>
                  <p><strong>Postcodes:</strong> {(rep.postcodes || []).join(", ")}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add new rep */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3">Add New Rep</h2>
        <form onSubmit={addNewRep} className="grid gap-3">
          <input
            className="p-3 border rounded-md"
            placeholder="Name"
            value={newRep.name}
            onChange={(e) => setNewRep((s) => ({ ...s, name: e.target.value }))}
          />
          <input
            className="p-3 border rounded-md"
            placeholder="Email"
            value={newRep.email}
            onChange={(e) => setNewRep((s) => ({ ...s, email: e.target.value }))}
          />
          <input
            className="p-3 border rounded-md"
            placeholder="Phone"
            value={newRep.phone}
            onChange={(e) => setNewRep((s) => ({ ...s, phone: e.target.value }))}
          />
          <textarea
            className="p-3 border rounded-md"
            placeholder="Postcodes (comma-separated: e.g., TF, N, EC, BN)"
            value={newRep.postcodes}
            onChange={(e) => setNewRep((s) => ({ ...s, postcodes: e.target.value }))}
            rows={2}
          />
          <button
            type="submit"
            disabled={adding}
            className="justify-self-start px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-semibold disabled:opacity-60"
          >
            {adding ? "Adding…" : "Add Rep"}
          </button>
        </form>
      </div>

      {/* Existing reps */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">All Reps</h2>
          {loading && <span className="text-gray-500 text-sm">Loading…</span>}
        </div>

        {reps.length === 0 && !loading && (
          <p className="text-gray-500">No reps found.</p>
        )}

        <div className="grid gap-4">
          {reps.map((rep) => {
            const d = drafts[rep.id] || { name: "", email: "", phone: "", postcodes: "", newCode: "" };
            const chips = normalizeOutcodeArray(d.postcodes);
            return (
              <div key={rep.id} className="border rounded-md p-4">
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Name</label>
                    <input
                      className="w-full p-2 border rounded"
                      value={d.name}
                      onChange={(e) => setDraft(rep.id, { name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <input
                      className="w-full p-2 border rounded"
                      value={d.email}
                      onChange={(e) => setDraft(rep.id, { email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone</label>
                    <input
                      className="w-full p-2 border rounded"
                      value={d.phone}
                      onChange={(e) => setDraft(rep.id, { phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Add Postcode</label>
                    <div className="flex gap-2">
                      <input
                        className="flex-1 p-2 border rounded"
                        placeholder="e.g., TF3 or N1 (we'll store TF/N)"
                        value={d.newCode}
                        onChange={(e) => setDraft(rep.id, { newCode: e.target.value })}
                      />
                      <button
                        type="button"
                        onClick={() => addSinglePostcode(rep.id)}
                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <label className="block text-sm font-medium mb-1">Postcodes (comma-separated)</label>
                  <textarea
                    className="w-full p-2 border rounded"
                    rows={2}
                    value={d.postcodes}
                    onChange={(e) => setDraft(rep.id, { postcodes: e.target.value })}
                  />
                  {chips.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {chips.map((c) => (
                        <span
                          key={c}
                          className="inline-flex items-center gap-1 px-2 py-1 text-sm border rounded-full"
                        >
                          {c}
                          <button
                            type="button"
                            onClick={() => removeSinglePostcode(rep.id, c)}
                            className="text-red-600"
                            aria-label={`Remove ${c}`}
                            title="Remove"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => saveRep(rep.id, d)}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-semibold"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => deleteRep(rep.id)}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Toast */}
      {toast.msg && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-3 rounded shadow text-white ${
            toast.type === "error" ? "bg-red-600" : "bg-emerald-600"
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default SalesRepManager;
